import * as React from 'react';
import { compose, setDisplayName, withProps } from 'recompose';
import { connect } from 'react-redux';
import * as moment from 'moment';
import { translate, TranslationFunction } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { State } from 'reducers';
import { ReportId, ReportMap, UtcTime } from 'types';
import { Column, TableCellProps } from 'react-virtualized';
import { Table } from 'ui/shared/Table';
import { isLoadingReports, getReports, getReportsError } from 'selectors/reportSelectors';
import { getTimezoneOffsetCalculator } from 'selectors/serverSelectors';
import { Calendar, UserGroup } from 'ui/shapes';
import { getReportNameTextKey } from 'logic/reports/reportType';
import { editReport } from 'actions/reportActions';

import './ReportsTable.css';

const EmptyListView = ({ t }: TranslateProps) =>
    <div className="no-reports">
        { t('6.1241.0.13') }
    </div>
;

// We're only passing Report IDs to the table as input... so we need a lookup function to
// return the cell contents based on a report ID and a column name. This is used for
// sorting the columns.
const makeCellGetter = (reportMap: ReportMap, t: TranslationFunction) =>
    (reportId: ReportId, column: string | null) => {
        const report = reportMap[reportId];
        if (!report) return '';

        switch (column || 'name') {
            case 'name':
                return report.name;

            case 'type':
                return t(getReportNameTextKey(report.reportType));

            case 'lastRun':
                return report.lastRun;

            case 'shared':
                return !!report.shared;

            case 'scheduled':
                return !!report.scheduled;

            case 'created':
                return report.created;
            
            default:
                throw new Error(`Unexpected column: ${column}`);
        }
    }
;

type Props = StateProps & RecomposeProps & OwnProps & OemProps & TranslateProps & DispProps;

export const RawReportsTable = ({
    reportIds,
    selectedReportId,
    handleSelectReportId,
    goToReport,
    rowStyle,
    reportMap,
    tzOffsetCalculator,
    styles,
    t,
}: Props) =>
    <div id="reports-table" className="abs">
        <Table
            items={ reportIds }
            noRowsRenderer={ () => <EmptyListView t={ t }/> }
            onClickRowItem={ handleSelectReportId }
            onDoubleClickRowItem={ goToReport }
            cellGetter={ makeCellGetter(reportMap, t) }
            rowStyle={ rowStyle }
            initialSortBy="name"
        >
            <Column
                dataKey="name"
                width={ 300 }
                label={ t('193') }
                disableSort={ false }
                cellRenderer={
                    ({ rowData: reportId }: TableCellProps) => {
                        const { name } = reportMap[reportId];
                        return name;
                    }
                }
                flexGrow={ 1 }
            />
            <Column
                dataKey="type"
                width={ 200 }
                label={ t('1158') }
                disableSort={ false }
                cellRenderer={
                    ({ rowData: reportId }: TableCellProps) => {
                        const { reportType } = reportMap[reportId];
                        return t(getReportNameTextKey(reportType));
                    }
                }
            />
            <Column
                dataKey="shared"
                width={ 80 }
                label={ t('6.9622.3766') }
                disableSort={ false }
                cellRenderer={
                    ({ rowData: reportId }: TableCellProps) => {
                        const { shared } = reportMap[reportId];
                        return shared
                            ? <span title={ t('6.9622.3766') } style={ styles.centerBlock }>
                                <UserGroup
                                    width="1em"
                                    height="1em"
                                    style={ selectedReportId === reportId ? styles.FillInvertForeground : styles.FillForeground }
                                />
                            </span>
                            : '';
                    }
                }
            />
            <Column
                dataKey="scheduled"
                width={ 80 }
                label={ t('Scheduled') }
                disableSort={ false }
                cellRenderer={
                    ({ rowData: reportId }: TableCellProps) => {
                        const { scheduled } = reportMap[reportId];
                        return scheduled
                            ? <span title={ t('Scheduled') } style={ styles.centerBlock }>
                                <Calendar
                                    width="1em"
                                    height="1em"
                                    style={ selectedReportId === reportId ? styles.FillInvertForeground : styles.FillForeground }
                                />
                            </span>
                            : '';
                    }
                }
            />
            <Column
                dataKey="lastRun"
                width={ 250 }
                label={ t('6.1241.0.11') }
                disableSort={ false }
                cellRenderer={
                    ({ rowData: reportId }: TableCellProps) => {
                        const { lastRun, lastScheduleRun } = reportMap[reportId];
                        const lastRunEx = Math.max(lastRun || 0, lastScheduleRun || 0) || null;
                        return lastRunEx && moment
                            .utc(lastRunEx)
                            .utcOffset(tzOffsetCalculator(lastRunEx))
                            .fromNow();
                    }
                }
            />
            <Column
                dataKey="created"
                width={ 250 }
                label={ t('6.1241.0.12') }
                disableSort={ false }
                cellRenderer={
                    ({ rowData: reportId }: TableCellProps) => {
                        const { created } = reportMap[reportId];
                        return created && moment
                            .utc(created)
                            .utcOffset(tzOffsetCalculator(created))
                            .fromNow() || null;
                    }
                }
            />
        </Table>
    </div>
;

type OwnProps = {
    selectedReportId: ReportId | null;
    onSelect: (reportId: ReportId | null) => void;
};
type DispProps = {
    goToReport: (reportId: ReportId) => void;
};
type RecomposeProps = {
    reportIds: ReportId[];
    handleSelectReportId: (reportId: ReportId) => void;
    rowStyle: (index: number) => {}; // Calculate the row style based on the index
};
type StateProps = {
    reportMap: ReportMap;
    loading: boolean;
    error: Error | null;
    tzOffsetCalculator: (t: UtcTime) => number;
};

const mapStateToProps = (state: State, { selectedReportId }: OwnProps) => ({
    reportMap: getReports(state),
    loading: isLoadingReports(state),
    error: getReportsError(state),
    tzOffsetCalculator: getTimezoneOffsetCalculator(state),
});

const mapDispatchToProps = (dispatch: Function): DispProps => ({
    goToReport: reportId => dispatch(editReport(reportId, { copy: false })),
});

export const ReportsTable = compose<Props, OwnProps>(
    setDisplayName('ReportsTable'),
    skin,
    translate(),
    connect<StateProps, DispProps, OwnProps>(mapStateToProps, mapDispatchToProps),
    withProps(({ reportMap }: StateProps & OwnProps) => ({
        reportIds: Object.keys(reportMap).filter(id => !reportMap[id].deleted),
    })),
    withProps(({ reportIds, onSelect, goToReport, selectedReportId, styles }: RecomposeProps & OemProps & OwnProps & DispProps) => ({
        handleSelectReportId: (reportId: ReportId) => {
            onSelect(reportId === selectedReportId ? null : reportId);
        },
        rowStyle: ({ index, item }: { index: number; item: ReportId }) => {
            // Note: the index is for the *sorted* order and doesn't match our reportIds array
            return (index >= 0 && selectedReportId && item === selectedReportId)
                ? styles.TR_Selected
                : styles.TR;
        },
        styles: {
            ...styles,
            centerBlock: {
                display: 'block',
                textAlign: 'center',
            },
        },
    })),
)(RawReportsTable);



// WEBPACK FOOTER //
// ./src/ui/pages/reports/ReportsTable.tsx